import { useState, type FormEvent } from "react";
import { useParams } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Adjunto, type Categoria, type Cotizacion, type CotizacionDetalle, type LineaCotizacion } from "@/lib/api";
import {
  aPayloadLinea,
  avisoDiferenciaLineas,
  avisosAceptar,
  estaVencida,
  formLinea,
  nombreItemVinculable,
  opcionesItemVinculable,
  opcionesSubcategoria,
  queryMismaSubcategoria,
  quedanNoElegidos,
  textoSubcategoria,
  validarLinea,
  type FormLinea,
  type PayloadLinea,
} from "@/lib/cotizaciones";
import { hoyISO } from "@/lib/format";
import { opcionesUnidad, subtotalForm } from "@/lib/presupuesto";

const SIN_ERRORES: Partial<Record<keyof FormLinea, string>> = {};
type Dialogo = { tipo: "linea"; linea: LineaCotizacion | null } | { tipo: "borrar"; linea: LineaCotizacion } | { tipo: "aceptar" } | null;

/**
 * Lógica de la ficha de un presupuesto recibido (sin JSX): sus datos, sus adjuntos y su desglose en líneas (alta,
 * edición y baja), cada una vinculable a un ítem de su categoría. El admin cambia la subcategoría al toque (los de
 * antes sin subcategoría se ubican así; Mati, 2026-10-30). El total sigue
 * siendo el cargado a mano: si las líneas no lo suman, se avisa. Ven admin y contador; escribe el admin. Aceptar se hace
 * acá, con un resumen, lo que falta revisar y los que quedan como no elegidos (Mati, 2026-10-29).
 */
export function useCotizacionTab() {
  const { fid, membresia } = useContextoApp();
  const { id } = useParams();
  const cotizacionId = Number(id);
  const base = `/api/fideicomisos/${fid}/cotizaciones/${cotizacionId}`;
  const esAdmin = membresia.rol === "admin";

  const recurso = useApiResource<CotizacionDetalle>(Number.isInteger(cotizacionId) ? base : null);
  const d = recurso.data;

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormLinea>(() => formLinea());
  const [mostrarErrores, setMostrarErrores] = useState(false);

  const accionGuardar = useAsyncAction((linea: LineaCotizacion | null, datos: PayloadLinea) =>
    linea ? api.patch<LineaCotizacion>(`${base}/lineas/${linea.id}`, datos) : api.post<LineaCotizacion>(`${base}/lineas`, datos),
  );
  const accionBorrar = useAsyncAction((lineaId: number) => api.delete<void>(`${base}/lineas/${lineaId}`).then(() => true));
  const accionSubir = useAsyncAction((archivo: File) => {
    const datos = new FormData();
    datos.append("archivo", archivo);
    return api.upload<Adjunto>(`${base}/adjuntos`, datos);
  });
  const aceptarAbierto = dialogo?.tipo === "aceptar";
  const mismaSubcategoria = useApiResource<Cotizacion[]>(aceptarAbierto && d ? `/api/fideicomisos/${fid}/cotizaciones${queryMismaSubcategoria(d)}` : null);
  const categorias = useApiResource<Categoria[]>(esAdmin ? `/api/fideicomisos/${fid}/presupuesto/categorias` : null);
  const accionSubcategoria = useAsyncAction((itemId: number) => api.patch<Cotizacion>(base, { item_id: itemId }));
  const accionAceptar = useAsyncAction(() => api.patch<Cotizacion>(base, { estado: "aceptado" }));
  const accionBorrarAdjunto = useAsyncAction((adjuntoId: number) => api.delete<void>(`/api/fideicomisos/${fid}/adjuntos/${adjuntoId}`).then(() => true));

  const errores = validarLinea(form);
  const cerrar = () => setDialogo(null);

  const abrirForm = (linea: LineaCotizacion | null) => {
    setForm(formLinea(linea ?? undefined));
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    setDialogo({ tipo: "linea", linea });
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "linea" || Object.keys(errores).length > 0) return;
    if (await accionGuardar.run(dialogo.linea, aPayloadLinea(form))) {
      cerrar();
      recurso.recargar();
    }
  };

  const confirmarBorrar = async () => {
    if (dialogo?.tipo !== "borrar") return;
    if (await accionBorrar.run(dialogo.linea.id)) {
      cerrar();
      recurso.recargar();
    }
  };

  const confirmarAceptar = async () => {
    if (await accionAceptar.run()) {
      cerrar();
      recurso.recargar();
    }
  };

  const vinculables = d?.items_vinculables ?? [];
  const nombreItem = (itemId: number | null) => {
    const item = itemId === null ? undefined : vinculables.find((i) => i.id === itemId);
    return item ? nombreItemVinculable(item) : null;
  };

  return {
    esAdmin,
    base,
    recargar: recurso.recargar,
    detalle: d ?? null,
    cargando: recurso.loading && !d,
    error: d ? null : (recurso.error?.message ?? null),
    vencida: d ? estaVencida(d, hoyISO()) : false,
    /** Las líneas y el precio van en la moneda del presupuesto (ARS mientras no tenga una). */
    moneda: d?.moneda ?? "ARS",
    avisoDiferencia: d ? avisoDiferenciaLineas(d) : null,
    sinVinculables: d !== undefined && vinculables.length === 0,
    nombreItem,
    rutaEditar: `/cotizaciones?editar=${cotizacionId}`,
    // aceptar
    puedeAceptar: esAdmin && d !== undefined && d.monto !== null && d.estado !== "aceptado",
    abrirAceptar: () => {
      accionAceptar.limpiarError();
      setDialogo({ tipo: "aceptar" });
    },
    avisosAceptar: d ? avisosAceptar(d, hoyISO()) : [],
    /** null mientras carga. */
    quedanNoElegidos: d && mismaSubcategoria.data ? quedanNoElegidos(d, mismaSubcategoria.data) : null,
    confirmarAceptar,
    aceptando: accionAceptar.loading,
    errorAceptar: accionAceptar.error ?? (aceptarAbierto && !mismaSubcategoria.data ? (mismaSubcategoria.error?.message ?? null) : null),
    // subcategoría
    subcategoria: d ? textoSubcategoria(d) : "",
    opcionesSubcategoria: opcionesSubcategoria(categorias.data ?? []),
    cambiarSubcategoria: async (valor: string) => {
      if (!d || valor === String(d.item_id)) return;
      if (await accionSubcategoria.run(Number(valor))) recurso.recargar();
    },
    guardandoSubcategoria: accionSubcategoria.loading,
    errorSubcategoria: accionSubcategoria.error ?? (categorias.data ? null : (categorias.error?.message ?? null)),
    // líneas
    dialogo,
    cerrar,
    abrirNueva: () => abrirForm(null),
    abrirEditar: (l: LineaCotizacion) => abrirForm(l),
    abrirBorrar: (l: LineaCotizacion) => {
      accionBorrar.limpiarError();
      setDialogo({ tipo: "borrar", linea: l });
    },
    form,
    cambiar: <K extends keyof FormLinea>(campo: K, valor: FormLinea[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    opcionesUnidad: opcionesUnidad(form.unidad),
    opcionesItem: opcionesItemVinculable(vinculables),
    subtotal: subtotalForm(form),
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
    // adjuntos
    rutaDescarga: (a: Adjunto) => `/api/fideicomisos/${fid}/adjuntos/${a.id}/descarga`,
    // el PDF o la foto del presupuesto se ve en la ficha (Mati, 2026-10-29)
    rutaVer: (a: Adjunto) => `/api/fideicomisos/${fid}/adjuntos/${a.id}/descarga?ver=true`,
    subirAdjunto: async (archivo: File) => {
      accionBorrarAdjunto.limpiarError();
      if (await accionSubir.run(archivo)) recurso.recargar();
    },
    borrarAdjunto: async (a: Adjunto) => {
      accionSubir.limpiarError();
      if (await accionBorrarAdjunto.run(a.id)) recurso.recargar();
    },
    subiendo: accionSubir.loading,
    errorAdjuntos: accionSubir.error ?? accionBorrarAdjunto.error,
  };
}
